package dev.wyns.ventrova.app

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.Stable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.runtime.snapshotFlow
import androidx.lifecycle.viewmodel.compose.viewModel
import dev.wyns.ventrova.AppDependencies
import dev.wyns.ventrova.data.model.GeoPoint
import dev.wyns.ventrova.ui.map.PlacesMapController
import dev.wyns.ventrova.ui.map.rememberPlacesMapController
import kotlin.time.Duration.Companion.minutes
import kotlin.time.Duration.Companion.seconds
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.flow.filterNotNull
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.launch
import kotlinx.coroutines.withTimeoutOrNull
import org.maplibre.compose.location.LocationPermission
import org.maplibre.compose.location.LocationState
import org.maplibre.compose.location.rememberLocationState

/** Everything a platform layout needs, set up the same way on every platform. */
@Stable
class AppEnvironment internal constructor(
    val dependencies: AppDependencies,
    val viewModel: AppViewModel,
    val map: PlacesMapController,
    val location: LocationState,
    private val scope: CoroutineScope,
) {
    /** True while the onboarding waits for the location permission prompt to be answered. */
    var awaitingPermission by mutableStateOf(false)
        private set

    /** "Show what's around me": asks for the location permission, then finishes the onboarding. */
    fun useLocationFromOnboarding() {
        if (location.permission is LocationPermission.Granted) {
            viewModel.completeOnboarding(locationShared = true)
            centerOnUserWhenKnown()
            return
        }
        val before = location.permission
        if (before is LocationPermission.NotGranted && before.canRequest == false) {
            // The system won't show the prompt again; continue without location.
            viewModel.completeOnboarding(locationShared = false)
            return
        }
        awaitingPermission = true
        location.requestPermission()
        scope.launch {
            val answer = withTimeoutOrNull(PERMISSION_TIMEOUT) {
                snapshotFlow { location.permission }.first { it is LocationPermission.Granted || (it is LocationPermission.NotGranted && it != before) }
            }
            awaitingPermission = false
            val granted = answer is LocationPermission.Granted
            viewModel.completeOnboarding(locationShared = granted)
            if (granted) centerOnUserWhenKnown()
        }
    }

    fun skipLocationFromOnboarding() = viewModel.completeOnboarding(locationShared = false)

    /** The locate button: centers on the user, asking for permission first when needed. */
    fun locate() {
        val known = location.lastLocation?.position
        if (location.permission is LocationPermission.Granted && known != null) {
            scope.launch { map.animateTo(GeoPoint(known.latitude, known.longitude), zoom = maxOf(map.zoom, 15.75)) }
            viewModel.showMessage(AppMessage.Kind.YourLocation)
        } else {
            location.requestPermission()
            centerOnUserWhenKnown()
        }
    }

    fun focus(place: PlaceItem, fractionX: Float = 0.5f, fractionY: Float = 0.4f) {
        scope.launch { map.focus(place.place.location, fractionX, fractionY) }
    }

    fun zoomBy(delta: Double) {
        scope.launch { map.zoomBy(delta) }
    }

    /** At startup: centers on the user when the location permission was granted earlier. */
    internal fun centerOnUserIfAllowed() {
        scope.launch {
            val permission = snapshotFlow { location.permission }.first { it !is LocationPermission.Unknown }
            if (permission is LocationPermission.Granted && !viewModel.state.value.showOnboarding) centerOnUserWhenKnown()
        }
    }

    private fun centerOnUserWhenKnown() {
        scope.launch {
            // Give up after a while so the camera doesn't jump long after the user started panning.
            val position = withTimeoutOrNull(LOCATION_TIMEOUT) {
                snapshotFlow { location.lastLocation?.position }.filterNotNull().first()
            } ?: return@launch
            map.animateTo(GeoPoint(position.latitude, position.longitude), zoom = DefaultZoom)
        }
    }

    private companion object {
        val PERMISSION_TIMEOUT = 2.minutes
        val LOCATION_TIMEOUT = 15.seconds
    }
}

/**
 * Creates the environment. Pass [sharedViewModel] when several Compose hosts share one view model
 * (iOS embeds separate Compose views in native chrome).
 */
@Composable
fun rememberAppEnvironment(dependencies: AppDependencies, sharedViewModel: AppViewModel? = null): AppEnvironment {
    val viewModel = sharedViewModel ?: viewModel {
        AppViewModel(dependencies.placeRepository, dependencies.keyValueStore, dependencies.suggestionService)
    }
    val map = rememberPlacesMapController()
    val location = rememberLocationState()
    val scope = rememberCoroutineScope()

    LaunchedEffect(location) {
        snapshotFlow { location.lastLocation?.position }.collect { position ->
            viewModel.onUserLocation(position?.let { GeoPoint(it.latitude, it.longitude) })
        }
    }

    val environment = remember(viewModel, map, location) { AppEnvironment(dependencies, viewModel, map, location, scope) }
    LaunchedEffect(environment) { environment.centerOnUserIfAllowed() }
    return environment
}
